import { HttpClient, type RequestOptions } from './core/http';
import type { ClientOptions } from './core/types';
import { CheckerClient } from './modules/checker';
import { DatacenterClient } from './modules/datacenter';
import { DedicatedClient } from './modules/dedicated';
import { FreeProxyListClient } from './modules/free';
import type { FreeProxy, FreeProxyFilters } from './modules/free';
import { PublicIpClient } from './modules/ip';
import type { GetPublicIpOptions, PublicIpInfo } from './modules/ip';
import { MobileClient } from './modules/mobile';
import { ResidentialClient } from './modules/residential';
import { SerpClient } from './modules/serp';

/**
 * The single public class users import. Holds a configured HTTP client and
 * exposes each product as a namespace on the instance:
 *
 *   const ps = new ProxyScrape();           // free tier — no auth
 *   await ps.free.list({ protocol: 'socks5' });
 *
 *   const ps = new ProxyScrape({ apiKey });
 *   await ps.residential.acquire({ ... });  // throws until implemented
 *
 * Adding a new product later means:
 *   1. Create src/modules/<name>/{index.ts,client.ts,types.ts}
 *   2. Add the field below
 *   3. No change to the public API contract — existing callers are unaffected.
 */
export class ProxyScrape {
  /** Free, unauthenticated proxy list. v0.1 — implemented. */
  readonly free: FreeProxyListClient;

  /** Proxy checker. v0.1 — stub, throws on use. */
  readonly checker: CheckerClient;

  /** Residential proxy pool. v0.1 — stub. */
  readonly residential: ResidentialClient;

  /** Datacenter proxy pool. v0.1 — stub. */
  readonly datacenter: DatacenterClient;

  /** Dedicated datacenter proxies. v0.1 — stub. */
  readonly dedicated: DedicatedClient;

  /** Mobile proxies. v0.1 — stub. */
  readonly mobile: MobileClient;

  /** Google SERP API. v0.1 — stub. */
  readonly serp: SerpClient;

  // Held privately because the umbrella class wraps it via `getPublicIp()`
  // rather than exposing it as a namespace — IP lookup is single-method and
  // belongs at the top level, not as `ps.ip.get()`.
  private readonly publicIp: PublicIpClient;

  constructor(opts: ClientOptions = {}) {
    const http = new HttpClient(opts);
    this.free = new FreeProxyListClient(http);
    this.checker = new CheckerClient(http);
    this.residential = new ResidentialClient(http);
    this.datacenter = new DatacenterClient(http);
    this.dedicated = new DedicatedClient(http);
    this.mobile = new MobileClient(http);
    this.serp = new SerpClient(http);
    this.publicIp = new PublicIpClient(http);
  }

  /**
   * Shortcut for `client.free.random(filters)` — picks one matching free
   * proxy, or null if none match.
   *
   * Lives at the top level because "give me a proxy" is the canonical
   * first call into the SDK. Functionally identical to `.free.random()`;
   * use whichever reads better in your code.
   *
   *   const p = await ps.proxy({ country: 'us', anonymity: 'elite' });
   *   if (p) console.log(p.url);
   */
  proxy(filters: FreeProxyFilters = {}, opts: RequestOptions = {}): Promise<FreeProxy | null> {
    return this.free.random(filters, opts);
  }

  /**
   * Look up the caller's public IP address. Useful for proxy
   * before/after verification:
   *
   *   const real = await ps.getPublicIp();
   *   // ...route a request through a proxy...
   *   if (proxiedIp.ip === real.ip) console.warn('Proxy leaked real IP');
   *
   * Defaults to ipinfo.io (no key, ~50k req/month free, returns IP + geo).
   * Pass `{ service: 'ipify' }` for IP-only / unlimited, or `{ endpoint }`
   * to point at your own service. The caller's ProxyScrape API key is
   * never forwarded to the third-party provider.
   */
  getPublicIp(opts: GetPublicIpOptions = {}): Promise<PublicIpInfo> {
    return this.publicIp.get(opts);
  }
}
